import { useEffect, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '@/components/Layout'
import { fetchVendor, createVendor, updateVendor } from '@/lib/api'
import type { Vendor } from '@/lib/types'

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export default function VendorForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    name: '', slug: '', zone: 'sharjah', phone: '', whatsapp: '', email: '',
    address: '', specialty: '', rating: '', review_count: '', verified: false, is_active: true,
  })

  useEffect(() => {
    if (isEdit) {
      fetchVendor(id!).then(v => {
        if (v) {
          setForm({
            name: v.name, slug: v.slug, zone: v.zone, phone: v.phone ?? '', whatsapp: v.whatsapp ?? '',
            email: v.email ?? '', address: v.address ?? '', specialty: v.specialty ?? '',
            rating: v.rating != null ? String(v.rating) : '', review_count: String(v.review_count),
            verified: v.verified, is_active: v.is_active,
          })
        }
        setLoading(false)
      }).catch(() => setLoading(false))
    }
  }, [id, isEdit])

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        name: form.name,
        slug: form.slug || slugify(form.name),
        zone: form.zone,
        phone: form.phone || null,
        whatsapp: form.whatsapp || null,
        email: form.email || null,
        address: form.address || null,
        specialty: form.specialty || null,
        rating: form.rating ? Number(form.rating) : null,
        review_count: Number(form.review_count) || 0,
        verified: form.verified,
        is_active: form.is_active,
      }
      if (isEdit) await updateVendor(id!, payload)
      else await createVendor(payload)
      navigate('/vendors')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save vendor')
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
  const label = 'block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5'

  return (
    <Layout title={isEdit ? 'Edit Vendor' : 'Add Vendor'}>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6 md:p-8">
          {loading ? (
            <p className="text-slate-400 text-sm py-8 text-center">Loading...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={label}>Company name *</label>
                  <input className={input} value={form.name} onChange={e => set('name', e.target.value)} required
                    placeholder="Al Bustan Used Cars & Spare Parts" />
                </div>
                <div>
                  <label className={label}>Slug</label>
                  <input className={input} value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="auto-generated" />
                </div>
                <div>
                  <label className={label}>Zone *</label>
                  <select className={input} value={form.zone} onChange={e => set('zone', e.target.value)}>
                    <option value="sharjah">Sharjah</option>
                    <option value="ajman">Ajman</option>
                    <option value="al-quoz">Dubai · Al Quoz</option>
                  </select>
                </div>
                <div>
                  <label className={label}>Phone</label>
                  <input className={input} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+971 6 555 1234" />
                </div>
                <div>
                  <label className={label}>WhatsApp</label>
                  <input className={input} value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="97165551234" />
                </div>
                <div>
                  <label className={label}>Email</label>
                  <input type="email" className={input} value={form.email} onChange={e => set('email', e.target.value)} placeholder="info@company.ae" />
                </div>
                <div>
                  <label className={label}>Rating</label>
                  <input type="number" min="0" max="5" step="0.1" className={input} value={form.rating} onChange={e => set('rating', e.target.value)} placeholder="4.6" />
                </div>
                <div>
                  <label className={label}>Review count</label>
                  <input type="number" min="0" className={input} value={form.review_count} onChange={e => set('review_count', e.target.value)} placeholder="214" />
                </div>
                <div className="md:col-span-2">
                  <label className={label}>Specialty</label>
                  <input className={input} value={form.specialty} onChange={e => set('specialty', e.target.value)} placeholder="Mercedes & BMW used parts" />
                </div>
                <div className="md:col-span-2">
                  <label className={label}>Address</label>
                  <input className={input} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Souq Al Haraj, Sajaa, Sharjah" />
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={form.verified} onChange={e => set('verified', e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                  Verified
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                  Active
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-surface-100">
                <button type="submit" disabled={saving}
                  className="bg-brand-600 text-white font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                  {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Vendor'}
                </button>
                <button type="button" onClick={() => navigate('/vendors')}
                  className="bg-white border border-surface-200 text-slate-600 font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-surface-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  )
}
